/**
 * @file A controlled component that triggers loading for infinite scroll
 * behaviour.
 *
 * Should be instantiated positionally at the bottom of a list whose next load
 * should be triggered as the user scrolls to the bottom of the list.
 *
 * Implemented using the IntersectionObserver web API for optimal performance.
 */

import React, {
  useState,
  useEffect,
  useRef,
  ReactElement,
  Dispatch,
  SetStateAction,
} from 'react';
import PropTypes from 'prop-types';

import { Button } from '@wds/button';
import { Box } from '@wds/box';
import { ProgressCircle } from '@wds/progress-circle';

import { T, t } from '@sm/intl';

type InfiniteScrollTriggerProps = {
  /**
   * Set to `true` when there are more items that can possibly be loaded
   * (otherwise, should be set to `false`).
   */
  hasMoreToLoad: boolean;

  /**
   * Set to `true` when the items that will initially populate the list are
   * being loaded (otherwise, `false`).
   */
  loadingFirstData: boolean;

  /**
   * Set to `true` when more results are being loaded. Will show a loading
   * spinner underneath the list.
   */
  loadingMore: boolean;

  /**
   * Callback called when more list items are requested.
   */
  onLoadMore: (() => void) | (() => Promise<void>);
};

const COPY = {
  // todo: add in definemessages when translating
  LOAD_MORE: {
    id: 'InfiniteScrollTrigger.LoadMore',
    defaultMessage: 'Load more',
    description: '[Type: button][Vis: med] - Button to load more data',
  },
  LOADING: {
    id: 'InfiniteScrollTrigger.Loading',
    defaultMessage: 'Loading...',
    description: '[Type: button][Vis: low] - loading data',
  },
};

type IntersectionObserverOptions = {
  root?: HTMLElement | null;
  rootMargin?: string;
  threshold?: number;
};

const useIntersectionObserverCallback = (
  callback: (entry: IntersectionObserverEntry) => void,
  options: IntersectionObserverOptions = {}
): [boolean, Dispatch<SetStateAction<HTMLElement | null>>] => {
  const { root = null, rootMargin, threshold = 0 } = options;
  const [node, setNode] = useState<HTMLElement | null>(null);

  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (observer.current) {
      observer.current.disconnect();
    }

    if (window.IntersectionObserver) {
      observer.current = new window.IntersectionObserver(
        ([newEntry]) => callback(newEntry),
        {
          root,
          rootMargin,
          threshold,
        }
      );
    }

    if (node) {
      observer.current?.observe(node);
    }

    return () => observer.current?.disconnect();
  }, [callback, node, root, rootMargin, threshold]);

  return [!!window.IntersectionObserver, setNode];
};

function InfiniteScrollTrigger({
  hasMoreToLoad,
  loadingFirstData,
  loadingMore,
  onLoadMore,
}: InfiniteScrollTriggerProps): ReactElement {
  const [isSupported, ref] = useIntersectionObserverCallback(
    (entry: IntersectionObserverEntry) => {
      if (entry.isIntersecting) {
        onLoadMore();
      }
    },
    {}
  );

  return (
    <Choose>
      <When condition={loadingMore && !loadingFirstData}>
        <Box display="flex" flexJustify="center" mt={4}>
          <ProgressCircle
            continuous
            size="sm"
            data-testid="InfiniteScrollTrigger__Loading"
            valueText={t(COPY.LOADING)}
          />
        </Box>
      </When>
      <When condition={hasMoreToLoad && isSupported}>
        <Box mt={4}>
          <div ref={ref} />
        </Box>
      </When>
      <When condition={hasMoreToLoad && !isSupported}>
        <Box flexAlign="center" mt={4}>
          <Button color="muted" size="sm" onClick={onLoadMore}>
            <T desc={COPY.LOAD_MORE} />
          </Button>
        </Box>
      </When>
    </Choose>
  );
}

InfiniteScrollTrigger.propTypes = {
  hasMoreToLoad: PropTypes.bool.isRequired,
  onLoadMore: PropTypes.func.isRequired,
  loadingFirstData: PropTypes.bool.isRequired,
  loadingMore: PropTypes.bool.isRequired,
};

export default InfiniteScrollTrigger;
